import type { XY } from './cut-chart.view';
import { CutChartView } from './cut-chart.view';

type InteractionMode = 'VIEW' | 'DRAW_ARMED' | 'DRAW_PREVIEW' | 'DRAG_ENDPOINT';

export class CutChartInteractions {
  private mode: InteractionMode = 'VIEW';
  private slope: XY[] = [];
  private activeEndpoint: 0 | 1 | null = null;

  private rafId: number | null = null;
  private lastMove: MouseEvent | null = null;

  private zoomGloballyEnabled = true;

  // cursor / mouse state (for grab/grabbing in VIEW)
  private isMouseDown = false;

  // throttle emits
  private lastEmitTs = 0;
  private emitEveryMs = 60;

  constructor(
    private canvas: HTMLCanvasElement,
    private view: CutChartView,
    private canEdit: () => boolean,
    private isSlopeEditable: () => boolean,
    private eventToValues: (ev: MouseEvent) => { xValue: number; yValue: number },
    private emitDragging: (slope: XY[]) => void,
    private emitChanged: (slope: XY[]) => void,
  ) {}

  init(): void {
    this.applyModeToZoom();
    this.updateCursor();
  }

  destroy(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }

  setZoomGloballyEnabled(enabled: boolean): void {
    this.zoomGloballyEnabled = enabled;
    this.applyModeToZoom();
    this.updateCursor();
  }

  /** Store → state only (do not touch chart here) */
  setSlopeFromOutside(slope: XY[]): void {
    this.slope = slope.map(p => [p[0], p[1]] as XY);
  }

  /** Parent button */
  armDrawSlope(): void {
    if (!this.canEdit()) return;

    this.mode = 'DRAW_ARMED';
    this.activeEndpoint = null;

    this.applyModeToZoom();
    this.updateCursor();
  }

  cancelInteraction(): void {
    if (this.mode === 'VIEW') return;

    this.mode = 'VIEW';
    this.activeEndpoint = null;

    this.applyModeToZoom();
    this.updateCursor();
  }

  onMouseDown = (ev: MouseEvent): void => {
    if (!this.canEdit()) return;

    // left button => grabbing only for VIEW
    if (ev.button === 0) {
      this.isMouseDown = true;
      this.updateCursor();
    }

    // reserve ctrl if you want
    if (ev.ctrlKey) return;

    // -------------------------
    // DRAW: 2-click workflow
    // -------------------------

    // 1st click -> create slope + enter preview (no need to hold)
    if (this.mode === 'DRAW_ARMED') {
      const { xValue, yValue } = this.eventToValues(ev);

      this.slope = [[xValue, yValue], [xValue, yValue]];

      this.view.removeSlopeDataset();
      this.view.ensureSlopeDataset(this.slope, true);
      this.view.updateNone();

      this.mode = 'DRAW_PREVIEW';
      this.activeEndpoint = 1;

      this.applyModeToZoom();
      this.updateCursor();

      this.emitDraggingThrottled(true);
      return;
    }

    // 2nd click -> finalize slope and return to VIEW
    if (this.mode === 'DRAW_PREVIEW') {
      const { xValue, yValue } = this.eventToValues(ev);

      // finalize end point
      this.slope[1] = [xValue, yValue];
      this.view.setSlopePoint(1, this.slope[1]);
      this.view.updateNone();

      this.mode = 'VIEW';
      this.activeEndpoint = null;

      this.applyModeToZoom();
      this.updateCursor();

      this.emitChanged(this.cloneSlope());
      return;
    }

    // -------------------------
    // VIEW: drag endpoints
    // -------------------------
    if (this.mode === 'VIEW') {
      const hit = this.view.hitTestSlopeEndpoint(ev);
      if (hit !== null) {
        this.mode = 'DRAG_ENDPOINT';
        this.activeEndpoint = hit;

        this.applyModeToZoom();
        this.updateCursor();
      }
    }
  };

  onMouseMove = (ev: MouseEvent): void => {
    const canMove =
      this.mode === 'DRAW_PREVIEW' ||
      this.mode === 'DRAG_ENDPOINT';

    if (!canMove) return;
    if (this.activeEndpoint === null) return;

    this.lastMove = ev;
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      const e = this.lastMove;
      if (!e) return;

      const { xValue, yValue } = this.eventToValues(e);

      // update preview or endpoint drag
      this.slope[this.activeEndpoint!] = [xValue, yValue];
      this.view.setSlopePoint(this.activeEndpoint!, this.slope[this.activeEndpoint!]);
      this.view.updateNone();

      // only emit dragging during preview or endpoint drag
      this.emitDraggingThrottled(false);
    });
  };

  onMouseUp = (_ev: MouseEvent): void => {
    // release mouse => open hand again in VIEW
    this.isMouseDown = false;

    // IMPORTANT:
    // In DRAW_PREVIEW we do NOT finalize on mouseup.
    // User finalizes with 2nd click.
    if (this.mode === 'DRAW_PREVIEW') {
      this.updateCursor(); // stays crosshair
      return;
    }

    // endpoint drag ends on mouseup
    if (this.mode === 'DRAG_ENDPOINT') {
      this.mode = 'VIEW';
      this.activeEndpoint = null;

      this.applyModeToZoom();
      this.updateCursor();

      this.emitChanged(this.cloneSlope());
      return;
    }

    // otherwise just refresh cursor (VIEW -> grab)
    this.updateCursor();
  };

  onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.code === 'Escape') {
      this.cancelInteraction();
    }
  };

  private applyModeToZoom(): void {
    const isView = this.mode === 'VIEW';
    const zoomOn = isView && this.zoomGloballyEnabled;

    // prevent chart.js from receiving events while drawing/dragging endpoints
    if (!isView) this.view.disableChartEvents();
    else this.view.restoreChartEvents();

    this.view.setZoomToggle({ pan: zoomOn, wheel: zoomOn, pinch: zoomOn });
  }

  private updateCursor(): void {
    // if editing isn't allowed (collapsed), keep normal cursor
    if (!this.canEdit()) {
      this.canvas.style.cursor = 'default';
      return;
    }

    switch (this.mode) {
      case 'DRAW_ARMED':
      case 'DRAW_PREVIEW':
        this.canvas.style.cursor = 'crosshair';
        return;

      case 'DRAG_ENDPOINT':
        this.canvas.style.cursor = 'grabbing';
        return;

      case 'VIEW':
      default:
        this.canvas.style.cursor = this.isMouseDown ? 'grabbing' : 'grab';
        return;
    }
  }

  private emitDraggingThrottled(force: boolean): void {
    const now = performance.now();
    if (!force && (now - this.lastEmitTs) < this.emitEveryMs) return;
    this.lastEmitTs = now;
    this.emitDragging(this.cloneSlope());
  }

  private cloneSlope(): XY[] {
    return this.slope.map(p => [p[0], p[1]] as XY);
  }

  get isBusy(): boolean {
    return this.mode !== 'VIEW';
  }

  getSlope(): XY[] {
    return this.cloneSlope();
  }

  get isDrawing(): boolean {
    return this.mode === 'DRAW_ARMED' || this.mode === 'DRAW_PREVIEW';
  }
}
